import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/types';
import {
  getUserAccess,
  updateUserAccess,
  saveUserAccess,
  restoreUserAccess
} from '../../../redux/UserManagement/actions';
import { ROLE_PERMISSIONS } from '../constants/permissions';

export const useUserAccessRedux = () => {
  const dispatch = useDispatch();
  
  // Select user access state from Redux store
  const userAccess = useSelector((state: any) => state.userManagement?.userAccess);
  
  // Get current user from auth state
  const currentUser = useSelector((state: any) => state.auth?.user);

  // Initialize permissions for all roles
  const initializePermissions = useCallback(() => {
    const initialPermissions: Record<string, Record<string, boolean>> = {};

    // Find permissions for each role from the new array structure
    ROLE_PERMISSIONS.forEach((roleData: any) => {
      const roleName = roleData.group_name;
      const rolePerms = roleData.permissions[roleName] || [];
      
      initialPermissions[roleName] = {};
      rolePerms.forEach((perm: { id: string; value?: boolean }) => {
        // Use the permission's value property if available, otherwise default to true
        initialPermissions[roleName][perm.id] = perm.value !== undefined ? perm.value : true;
      });
    });

    return initialPermissions;
  }, []);

  // Fetch user access permissions from database
  const fetchUserAccess = useCallback(() => {
    dispatch(getUserAccess());
  }, [dispatch]);

  // Update a specific permission for a role
  const updatePermission = useCallback((role: string, permissionId: string, checked: boolean) => {
    // Update Redux state
    dispatch(updateUserAccess(role, permissionId, checked));
    
    // Also update the local ROLE_PERMISSIONS structure to keep it in sync
    if (userAccess?.rolePermissions?.data && Array.isArray(userAccess.rolePermissions.data)) {
      const roleIndex = userAccess.rolePermissions.data.findIndex((r: any) => r.group_name === role);
      if (roleIndex !== -1) {
        const roleData = userAccess.rolePermissions.data[roleIndex];
        if (roleData?.permissions && roleData.permissions[role]) {
          const permissionIndex = roleData.permissions[role].findIndex((p: any) => p.id === permissionId);
          if (permissionIndex !== -1 && permissionIndex !== undefined && permissionIndex !== null) {
            // Update existing permission
            if (roleData.permissions[role][permissionIndex].value !== undefined) {
              roleData.permissions[role][permissionIndex].value = checked;
            }
          } else {
            // Add new permission if it doesn't exist
            if (Array.isArray(roleData.permissions[role])) {
              roleData.permissions[role].push({
                id: permissionId,
                name: permissionId, 
                value: checked,
                created_date: new Date().toISOString(),
                update_date: new Date().toISOString()
              });
            }
          }
        }
      }
    }
  }, [dispatch, userAccess]);

  // Save all permissions to database
  const savePermissions = useCallback((rolePermissionsData: any[]) => {
    const updatedBy = currentUser?.id || 'system';
    // Pass the array directly since the API expects the new structure
    dispatch(saveUserAccess(rolePermissionsData, updatedBy));
  }, [dispatch, currentUser]);

  // Restore permissions from database
  const restorePermissions = useCallback(() => {
    dispatch(restoreUserAccess());
  }, [dispatch]);

  // Get current role permissions
  const getRolePermissions = useCallback(() => {
    return userAccess.rolePermissions;
  }, [userAccess.rolePermissions]);

  // Get the current state of a specific permission
  const getPermissionState = useCallback((role: string, permissionId: string): boolean => {
    // First check Redux state - handle the new data structure
    if (userAccess?.rolePermissions?.data && Array.isArray(userAccess.rolePermissions.data)) {
      const roleData = userAccess.rolePermissions.data.find((r: any) => r.group_name === role);
      if (roleData?.permissions && roleData.permissions[role]) {
        const permission = roleData.permissions[role].find((p: any) => p.id === permissionId);
        if (permission?.value !== undefined) {
          return permission.value;
        }
      }
    }
    
    // Fall back to the local ROLE_PERMISSIONS structure
    const roleData = ROLE_PERMISSIONS.find((r: any) => r.group_name === role);
    if (roleData?.permissions && roleData.permissions[role]) {
      const permission = roleData.permissions[role].find((p: any) => p.id === permissionId);
      return permission?.value || false;
    }
    
    return false;
  }, [userAccess]);

  // Check if permissions are being saved
  const isSaving = useCallback(() => {
    return userAccess.saving;
  }, [userAccess.saving]);

  // Check if permissions are being restored
  const isRestoring = useCallback(() => {
    return userAccess.restoring;
  }, [userAccess.restoring]);

  // Check if permissions are being loaded
  const isLoading = useCallback(() => {
    return userAccess.loading;
  }, [userAccess.loading]);

  // Get last saved timestamp
  const getLastSaved = useCallback(() => {
    return userAccess.lastSaved;
  }, [userAccess.lastSaved]);

  // Get last restored timestamp
  const getLastRestored = useCallback(() => {
    return userAccess.lastRestored;
  }, [userAccess.lastRestored]);

  // Get any error messages
  const getError = useCallback(() => {
    return userAccess.error;
  }, [userAccess.error]);

  return {
    // State
    rolePermissions: userAccess.rolePermissions,
    loading: userAccess.loading,
    saving: userAccess.saving,
    restoring: userAccess.restoring,
    error: userAccess.error,
    lastSaved: userAccess.lastSaved,
    lastRestored: userAccess.lastRestored,
    
    // Actions
    fetchUserAccess,
    updatePermission,
    savePermissions,
    restorePermissions,
    initializePermissions,
    
    // Utility functions
    getRolePermissions,
    getPermissionState,
    isSaving,
    isRestoring,
    isLoading,
    getLastSaved,
    getLastRestored,
    getError
  };
};
